import * as React from "react";
import { Grid, Stack } from "@mui/material";
import {
    useGetCclGCUserAccountDetailQuery,
    useGetCclUserAccountDetailQuery,
    useGetCclUserAuth0DetailQuery,
} from "../../../../services/cclTokenedGrandCentralApi";
import ComponentLoader from "../../../../components/common/componentLoader";
import { UserClaimsService } from "../../../../services/currentUserService/currentUserService";
import GrandCentralIdentitiesCard from "./grandCentralIdentitiesCard";
import GrandCentralEmailsCard from "./grandCentralEmailsCard";
import Auth0DetailsCard from "./auth0DetailCard";
import UserDetailsCard from "./userDetailsCard";
import D365DetailsCard from "./d365DetailsCard";
import { Profile } from "../../../../services/types/enterpriseCoachingApiTypes";
import GrandCentralConsentCard from "./grandCentralConsentCard";

interface UserGeneralPanelProps {
    emailAddress: string;
    imkey: number;
    profile: Profile;
}

const UserGeneralPanel: React.FC<UserGeneralPanelProps> = (props) => {
    const { data: enterpriseData } = useGetCclUserAccountDetailQuery(props.emailAddress, {
        skip: props.emailAddress == null || props.emailAddress === "",
    });

    const claimsService = new UserClaimsService();
    const isUserAdmin = claimsService.IsUserSuperAdmin();

    const {
        data: auth0Data,
        isLoading: auth0Loading,
        isSuccess: auth0Success,
    } = useGetCclUserAuth0DetailQuery(props.emailAddress, {
        skip: props.emailAddress == null || props.emailAddress === "",
    });
    const { data: grandCentralData, isLoading: grandCentralLoading } =
        useGetCclGCUserAccountDetailQuery(props.emailAddress, {
            skip: props.emailAddress == null || props.emailAddress === "",
        });

    if (auth0Loading || grandCentralLoading) {
        return <ComponentLoader msg={"Loading User Data"} />;
    }

    if (auth0Success) {
        return (
            <Stack height={1} width={1}>
                <Grid container spacing={2} alignItems="stretch">
                    {enterpriseData != null && !auth0Loading ? (
                        <Grid item xs={6} sx={{ display: "flex" }}>
                            <UserDetailsCard
                                gcDetail={grandCentralData ?? null}
                                enterpriseDetails={enterpriseData ?? null}
                                gcAccountEmail={props.emailAddress}
                                imKey={props.imkey}
                            />
                        </Grid>
                    ) : null}
                    <Grid item xs={6} sx={{ display: "flex" }}>
                        <D365DetailsCard
                            emailAddress={props.emailAddress}
                            profile={props.profile}
                        />
                    </Grid>
                    {claimsService.IsUserCclAdminOrSuperAdmin() && grandCentralData ? (
                        <Grid item xs={6} sx={{ display: "flex" }}>
                            <GrandCentralConsentCard
                                gcAccountId={grandCentralData.accountId}
                                gcAccountEmail={props.emailAddress}
                                isSuperAdmin={claimsService.IsUserSuperAdmin()}
                                imKey={props.imkey.toString()}
                            />
                        </Grid>
                    ) : null}
                    {grandCentralData != null ? (
                        <Grid item xs={6} sx={{ display: "flex" }}>
                            <GrandCentralIdentitiesCard
                                gcAccountId={grandCentralData.accountId}
                                gcIdentities={grandCentralData.identities}
                                gcAccountEmail={props.emailAddress}
                                isUserAdmin={isUserAdmin}
                                tenantId={grandCentralData.users[0]?.tenantId ?? ""}
                                imKey={props.imkey.toString()}
                            />
                        </Grid>
                    ) : null}
                    {auth0Data != null && (
                        <Grid item xs={6} sx={{ display: "flex" }}>
                            <Auth0DetailsCard
                                gcAccountEmail={props.emailAddress}
                                auth0Detail={auth0Data}
                                isUserAdmin={isUserAdmin}
                                imKey={props.imkey.toString()}
                            />
                        </Grid>
                    )}
                    {grandCentralData != null ? (
                        <Grid item xs={6} sx={{ display: "flex" }}>
                            <GrandCentralEmailsCard
                                gcAccountId={grandCentralData.accountId}
                                gcEmails={grandCentralData.emails}
                                gcAccountEmail={props.emailAddress}
                                isUserAdmin={isUserAdmin}
                                imKey={props.imkey.toString()}
                            />
                        </Grid>
                    ) : null}
                </Grid>
            </Stack>
        );
    }

    return <span>No User Account Data Loaded</span>;
};

export default UserGeneralPanel;
